import { HeroSection } from './HeroSection.jsx';
import { BlockRenderer } from './blocks/BlockRenderer.jsx';
import { FadeIn } from '../animation/FadeIn.jsx';
import { buildBreadcrumbList, buildFaqPage, serializeJsonLd } from '../../lib/structured-data.js';

/**
 * Datengetriebener Renderer für Gambio-Hub-Seiten.
 * @param {object} props.content - JSON (hero, breadcrumb, sections, cta, structuredData?)
 * @param {string} props.siteUrl - Basis-URL für absolute Breadcrumb-Links
 */
export function GuidePage({ content, siteUrl }) {
  const { hero, breadcrumb = [], sections = [], cta, structuredData = [] } = content;

  const autoSchema = [];
  if (breadcrumb.length) autoSchema.push(buildBreadcrumbList(breadcrumb, siteUrl));
  const faqSection = sections.find((s) => s.type === 'faq');
  if (faqSection?.items?.length) autoSchema.push(buildFaqPage(faqSection.items));
  const allSchema = [...autoSchema, ...structuredData];

  return (
    <>
      <HeroSection
        headline={hero.headline}
        subheadline={hero.subheadline}
        description={hero.description}
        cta={hero.cta}
        backgroundGradient={hero.backgroundGradient}
        alignment="center"
      />

      {breadcrumb.length > 0 && (
        <nav
          aria-label="Breadcrumb"
          className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"
        >
          <div className="container mx-auto px-4 md:px-8 lg:px-12 py-3">
            <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              {breadcrumb.map((crumb, i) => (
                <li key={i} className="flex items-center gap-2">
                  {i > 0 && <span aria-hidden="true">/</span>}
                  {i < breadcrumb.length - 1 ? (
                    <a href={crumb.href} className="hover:text-accent-dark dark:hover:text-accent">
                      {crumb.label}
                    </a>
                  ) : (
                    <span className="text-gray-900 dark:text-gray-200" aria-current="page">
                      {crumb.label}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </nav>
      )}

      <BlockRenderer sections={sections} />

      {cta && (
        <section className="py-20 md:py-32 bg-primary text-white">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <FadeIn>
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl mb-4">
                  {cta.headline}
                </h2>
                {cta.description && <p className="text-xl mb-8">{cta.description}</p>}
                {cta.features?.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-4 mb-8">
                    {cta.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-accent">{'✓'}</span>
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                )}
                <a
                  href={cta.button.href}
                  className="inline-block bg-accent hover:bg-accent/90 text-white font-bold px-8 py-4 rounded-lg transition-colors"
                >
                  {cta.button.label}
                </a>
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      {allSchema.map((schema, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
        />
      ))}
    </>
  );
}

export default GuidePage;
